"use client";

import React, { useEffect, useId, useRef } from "react";
import Image from "next/image";
import { AnimatePresence, motion } from "framer-motion";
import { useOutsideClick } from "@/hooks/use-outside-click";
import { platformIcons, platformNames } from "@/utils/platform-specific-data";
import { addAffiliateParams } from "@/utils/affiliate-links";
import { Track } from "@/types/track.types";

interface Top5TracksModalProps {
  track: Track | null;
  onClose: () => void;
  getLayoutId: (trackName: string, type: string) => string;
}

export default function Top5TracksModal({
  track,
  onClose,
  getLayoutId,
}: Top5TracksModalProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    if (track) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [track, onClose]);

  useOutsideClick(ref, onClose);

  return (
    <>
      <AnimatePresence>
        {track && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-overlay/20 h-full w-full z-10"
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {track ? (
          <div className="fixed inset-0 z-[100] overflow-y-auto md:flex md:items-start md:justify-center md:p-4">
            <motion.button
              key={`button-close-${track.name}`}
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.05 } }}
              className="flex fixed top-2 right-2 md:absolute md:top-6 md:right-6 items-center justify-center bg-theme-neon text-on-accent border-1 border-line-strong rounded-full h-11 w-11 z-10"
              onClick={onClose}
              aria-label="Close track details"
            >
              <CloseIcon />
            </motion.button>
            <motion.div
              layoutId={getLayoutId(track.name, "card")}
              ref={ref}
              className="w-full min-h-full md:h-auto md:max-w-[500px] md:min-h-fit md:my-8 flex flex-col bg-surface-raised dark:bg-surface md:rounded-3xl md:shadow-xl"
            >
              <motion.div layoutId={getLayoutId(track.name, "image")}>
                <Image
                  priority
                  width={1000}
                  height={1000}
                  src={track.image_url}
                  alt={track.name}
                  className="w-full md:rounded-tr-lg md:rounded-tl-lg object-cover object-top"
                />
              </motion.div>

              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <motion.h3
                      layoutId={getLayoutId(track.name, "title")}
                      className="text-sm font-semibold"
                    >
                      {track.name}
                    </motion.h3>
                    <p className="text-xs text-subtle dark:text-muted mt-1">
                      {track.artists?.map((a) => a.name).join(", ") ||
                        track.artist ||
                        "Unknown Artist"}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-center justify-center gap-4 mt-6 pb-8">
                  {(() => {
                    const hasBeatportLink = track.links?.some(
                      (link: any) => link.platform === "beatport"
                    );
                    const allLinks = [...(track.links || [])];

                    if (!hasBeatportLink) {
                      allLinks.push({
                        platform: "beatport" as const,
                        url: "https://beatport.com",
                      });
                    }

                    return allLinks
                      .sort((a, b) => {
                        // Beatport always first
                        if (a.platform === "beatport") return -1;
                        if (b.platform === "beatport") return 1;
                        // Then sort alphabetically
                        return platformNames[a.platform].localeCompare(
                          platformNames[b.platform]
                        );
                      })
                      .map((link) => (
                        <motion.a
                          key={`${link.platform}-${link.url}`}
                          layoutId={getLayoutId(
                            track.name,
                            `button-${link.platform}`
                          )}
                          href={addAffiliateParams(link.url, link.platform)}
                          target="_blank"
                          rel={
                            link.platform === "beatport"
                              ? "nofollow sponsored"
                              : undefined
                          }
                          aria-label={`Listen on ${platformNames[link.platform]}`}
                          className="h-10 w-full flex space-x-2 text-center items-center justify-center px-2 py-1 rounded-md font-bold bg-theme-neon text-on-accent text-xs"
                        >
                          {platformIcons[link.platform]}
                          <span>{platformNames[link.platform]}</span>
                        </motion.a>
                      ));
                  })()}
                </div>
              </div>
            </motion.div>
          </div>
        ) : null}
      </AnimatePresence>
    </>
  );
}

const CloseIcon = () => {
  return (
    <motion.svg
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.05 } }}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M18 6l-12 12" />
      <path d="M6 6l12 12" />
    </motion.svg>
  );
};
